<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { ApiError } from '@/api/client';
import { formatHours } from '@/util/format';
import type { WeeklyStudyHours } from '@/api/types';
import Confetti from '@/components/Confetti.vue';
import LockInSlider from '@/components/LockInSlider.vue';
import SchoolScheduleForm from '@/components/SchoolScheduleForm.vue';

const auth  = useAuthStore();
const route = useRoute();

const KEY_PREFIX = 'lockin.onboarding.done.';

const dayLabels: Record<keyof WeeklyStudyHours, string> = {
  mon: 'Maandag', tue: 'Dinsdag', wed: 'Woensdag',
  thu: 'Donderdag', fri: 'Vrijdag', sat: 'Zaterdag', sun: 'Zondag',
};

// Sensible default suggestion: ~1h on weekdays, lighter weekend.
const DEFAULT_HOURS: WeeklyStudyHours = {
  mon: 1, tue: 1, wed: 1, thu: 1, fri: 0.5, sat: 1, sun: 1,
};

// Steps: 0 = welcome, 1 = weekly hours, 2 = school rooster, 3 = celebrate.
const step    = ref(0);
const hours   = ref<WeeklyStudyHours>({ ...DEFAULT_HOURS });
const saving  = ref(false);
const error   = ref<string | null>(null);

const schoolForm = ref<InstanceType<typeof SchoolScheduleForm> | null>(null);
const stepBusy   = ref(false);

// Latch — once the wizard appears we keep it open until `finish()` runs,
// even though slide 2 saves `weeklyStudyHours` (which would otherwise flip
// the gate to false and close the dialog before slide 3).
const open = ref(false);

// localStorage isn't reactive — we mirror it in a ref and resync whenever
// the active user changes (login as someone else, register, etc.).
const localDismissed = ref(false);
watch(() => auth.user?.id, (id) => {
  localDismissed.value = id ? localStorage.getItem(KEY_PREFIX + id) === '1' : false;
  // Different user means a fresh decision — drop the latch so the gate
  // can re-evaluate for them.
  open.value = false;
}, { immediate: true });

const onPublicRoute = computed(() => !!route.meta.public);

const shouldOpen = computed(() => {
  if (!auth.isAuthenticated || !auth.user) return false;
  if (onPublicRoute.value) return false;
  if (localDismissed.value) return false;
  return !auth.user.weeklyStudyHours;
});

watch(shouldOpen, (yes) => {
  if (yes && !open.value) {
    open.value = true;
    step.value = 0;
    error.value = null;
    hours.value = auth.user?.weeklyStudyHours
      ? { ...auth.user.weeklyStudyHours }
      : { ...DEFAULT_HOURS };
  }
}, { immediate: true });

const visible = open;

const totalHours = computed(() => Object.values(hours.value).reduce((a, b) => a + b, 0));

async function saveHoursAndAdvance() {
  if (totalHours.value <= 0) {
    error.value = 'Stel minstens een paar uur per week in — anders kan LIA niets plannen.';
    return;
  }
  error.value = null;
  saving.value = true;
  try {
    await auth.updateProfile({ weeklyStudyHours: hours.value } as any);
    step.value = 2;
  } catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Opslaan mislukt — probeer opnieuw.';
  } finally {
    saving.value = false;
  }
}

async function saveSchoolAndAdvance() {
  stepBusy.value = true;
  try {
    if (await schoolForm.value?.save()) step.value = 3;
  } finally {
    stepBusy.value = false;
  }
}

function finish() {
  const id = auth.user?.id;
  if (id) localStorage.setItem(KEY_PREFIX + id, '1');
  localDismissed.value = true;
  open.value = false;
  step.value = 0;
}
</script>

<template>
  <v-dialog
    :model-value="visible"
    persistent
    fullscreen
    transition="dialog-bottom-transition"
    scrim="rgba(0,0,0,0.5)"
  >
    <v-card class="onboarding">
      <!-- Step indicator -->
      <div class="onboarding__dots">
        <span :class="['dot', { 'dot--active': step === 0 }]" />
        <span :class="['dot', { 'dot--active': step === 1 }]" />
        <span :class="['dot', { 'dot--active': step === 2 }]" />
        <span :class="['dot', { 'dot--active': step === 3 }]" />
      </div>

      <!-- ─── Slide 1: What is LockIn ─── -->
      <div v-if="step === 0" class="onboarding__body">
        <div class="d-flex flex-column align-center" style="margin-top: 4vh;">
          <img src="/icons/icon-192.png" alt="LockIn" width="88" height="88" />
          <h1 class="onboarding__title">Welkom bij LockIn</h1>
          <p class="onboarding__lede">
            Je studieplanner die plant, telt, en vooral: motiveert.
          </p>
        </div>

        <ul class="feature-list">
          <li>
            <v-icon icon="mdi-calendar-clock" size="28" color="primary" />
            <div>
              <strong>Rooster</strong>
              <p>Plan je huiswerk per dag en start een focus-timer per blok.</p>
            </div>
          </li>
          <li>
            <v-icon icon="mdi-robot-happy-outline" size="28" color="primary" />
            <div>
              <strong>LIA</strong>
              <p>Je AI-planner verdeelt grote opdrachten automatisch over de week.</p>
            </div>
          </li>
          <li>
            <v-icon icon="mdi-trophy-outline" size="28" color="warning" />
            <div>
              <strong>Awards &amp; levels</strong>
              <p>Verdien punten, ga in level omhoog en unlock awards en streaks.</p>
            </div>
          </li>
        </ul>

        <v-spacer />
        <v-btn
          color="primary"
          block
          size="large"
          append-icon="mdi-arrow-right"
          @click="step = 1"
        >
          Volgende
        </v-btn>
      </div>

      <!-- ─── Slide 2: Weekly hours ─── -->
      <div v-else-if="step === 1" class="onboarding__body">
        <div class="text-center" style="margin-top: 2vh;">
          <v-icon icon="mdi-clock-outline" size="48" color="primary" />
          <h1 class="onboarding__title">Hoeveel huiswerk per week?</h1>
          <p class="onboarding__lede">
            Hiermee plant LIA je werk. Geen zin om dit nu te doen? Sla 't over — je kan het later in je profiel instellen.
          </p>
        </div>

        <div class="hours-grid">
          <div
            v-for="(label, day) in dayLabels"
            :key="day"
            class="d-flex align-center"
            style="gap: 12px;"
          >
            <span style="width: 88px; font-size: 14px;">{{ label }}</span>
            <LockInSlider
              v-model="hours[day as keyof WeeklyStudyHours]"
              :min="0" :max="6" :step="0.5"
              color="primary"
              :aria-label="`Uren op ${label}`"
              style="flex: 1;"
            />
            <span class="numeric" style="width: 64px; text-align: right; font-size: 14px;">
              {{ formatHours(hours[day as keyof WeeklyStudyHours]) }}
            </span>
          </div>
        </div>

        <div class="onboarding__total">
          Totaal: <strong class="numeric">{{ formatHours(totalHours) }}</strong> per week
        </div>

        <v-alert v-if="error" type="error" density="compact" class="mb-2" :text="error" />

        <v-spacer />
        <div class="onboarding__nav">
          <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="step = 0">
            Terug
          </v-btn>
          <v-btn variant="text" :disabled="saving" @click="step = 2">
            Sla over
          </v-btn>
          <v-btn
            color="primary"
            size="large"
            class="onboarding__nav-primary"
            :loading="saving"
            @click="saveHoursAndAdvance"
          >
            Opslaan
          </v-btn>
        </div>
      </div>

      <!-- ─── Slide 3: School rooster ─── -->
      <div v-else-if="step === 2" class="onboarding__body">
        <div class="text-center" style="margin-top: 2vh;">
          <v-icon icon="mdi-school-outline" size="48" color="primary" />
          <h1 class="onboarding__title">Wanneer zit je op school?</h1>
          <p class="onboarding__lede">
            Zet je vaste schooltijden, dan plant LIA je huiswerk daar netjes omheen. Geen school op een dag? Vink 'm uit.
          </p>
        </div>

        <div class="hours-grid">
          <SchoolScheduleForm ref="schoolForm" />
        </div>

        <v-spacer />
        <div class="onboarding__nav">
          <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="step = 1">
            Terug
          </v-btn>
          <v-btn variant="text" :disabled="stepBusy" @click="step = 3">
            Sla over
          </v-btn>
          <v-btn
            color="primary"
            size="large"
            class="onboarding__nav-primary"
            :loading="stepBusy"
            @click="saveSchoolAndAdvance"
          >
            Opslaan
          </v-btn>
        </div>
      </div>

      <!-- ─── Slide 3: Confetti welcome ─── -->
      <div v-else class="onboarding__body onboarding__body--celebrate">
        <Confetti />
        <div class="text-center">
          <div class="welcome-emoji">🎉</div>
          <h1 class="onboarding__title onboarding__title--big">Welkom bij LockIn!</h1>
          <p class="onboarding__lede">
            Je bent helemaal klaar. Tijd om je eerste blok te plannen.
          </p>
        </div>

        <v-spacer />
        <v-btn
          color="primary"
          block
          size="large"
          append-icon="mdi-rocket-launch-outline"
          @click="finish"
        >
          Aan de slag
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.onboarding {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--lockin-bg, #D7EEFB);
}

.onboarding__dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 16px 0 4px;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.15);
  transition: width 0.2s ease, background 0.2s ease;
}
.dot--active {
  width: 22px;
  border-radius: 4px;
  background: #1FA8F2;
}

.onboarding__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 20px calc(20px + env(safe-area-inset-bottom));
  max-width: 520px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
}
.onboarding__body--celebrate {
  justify-content: center;
}

.onboarding__title {
  font-size: 24px;
  font-weight: 700;
  margin: 12px 0 4px;
  text-align: center;
}
.onboarding__title--big {
  font-size: 30px;
}

.onboarding__lede {
  text-align: center;
  color: rgba(0, 0, 0, 0.65);
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.4;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.feature-list li {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  background: white;
  padding: 14px;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.feature-list strong {
  display: block;
  font-size: 15px;
  margin-bottom: 2px;
}
.feature-list p {
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.4;
}

.hours-grid {
  margin: 18px 0 8px;
  background: white;
  border-radius: 14px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.onboarding__total {
  text-align: center;
  margin: 6px 0 14px;
  color: rgba(0, 0, 0, 0.7);
  font-size: 14px;
}

.onboarding__nav {
  display: flex;
  gap: 8px;
  align-items: stretch;
  width: 100%;
  min-width: 0;        /* let children shrink instead of forcing horizontal scroll */
}
.onboarding__nav-primary {
  flex: 1 1 0;
  min-width: 0;
}

.welcome-emoji {
  font-size: 84px;
  line-height: 1;
  margin-bottom: 8px;
  animation: pop 0.6s cubic-bezier(0.18, 0.89, 0.32, 1.28) both;
}
@keyframes pop {
  0%   { transform: scale(0);   opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1);   opacity: 1; }
}
</style>
